import React, { useEffect, useState } from 'react';
import { Loader2, X } from 'lucide-react';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Textarea,
} from '@roger/ui-components';
import type { InterventionTemplateDetail } from '../../services/api/types.gen';
import { renderEditableInstructionFields } from '../../utils/goals';
import { addInterventionToVisit } from '../../services/api';
import { useToast } from '../../hooks/useToastContext';

interface InterventionFormData {
  instruction: string;
  isValid: boolean;
}

interface CreateInterventionFromTemplateCardProps {
  template: InterventionTemplateDetail | null;
  onChange: (data: InterventionFormData) => void;
  showSaveButton?: boolean;
  visitId?: string;
  patientGoalId?: string;
  onSave?: () => void;
  onCancel?: () => void;
}

const CreateInterventionFromTemplateCard: React.FC<
  CreateInterventionFromTemplateCardProps
> = ({
  template,
  onChange,
  showSaveButton = false,
  visitId,
  patientGoalId,
  onSave,
  onCancel,
}) => {
  const [instructionValues, setInstructionValues] = useState<
    Record<string, string>
  >({});
  const [freeTextInstruction, setFreeTextInstruction] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [currentInstruction, setCurrentInstruction] = useState('');
  const [isValid, setIsValid] = useState(false);
  const { error: showError } = useToast();

  const isFreeText = template === null;
  const editableKeys = isFreeText
    ? []
    : Object.entries(template.instruction)
        .filter(([, field]) => field.editable)
        .map(([key]) => key);

  const assembleInstruction = (values: Record<string, string>) => {
    if (isFreeText) return freeTextInstruction.trim();
    const entries = Object.entries(template.instruction).sort(
      ([a], [b]) => Number(a) - Number(b)
    );
    return entries
      .map(([key, field]) =>
        field.editable ? (values[key] ?? '') : field.text
      )
      .join(' ');
  };

  useEffect(() => {
    if (isFreeText) return;
    const initialInstruction = assembleInstruction({});
    const initialIsValid = editableKeys.length === 0;
    setCurrentInstruction(initialInstruction);
    setIsValid(initialIsValid);
    onChange({
      instruction: initialInstruction,
      isValid: initialIsValid,
    });
  }, []);

  const handleFreeTextInstructionChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    const value = e.target.value;
    setFreeTextInstruction(value);
    // Report free-text state to the parent, so embedded (no save button)
    // usages like CreateGoalFromTemplateCard can validate and collect it.
    onChange({ instruction: value.trim(), isValid: !!value.trim() });
  };

  const handleInstructionChange = (key: string, value: string) => {
    const newValues = { ...instructionValues, [key]: value };
    setInstructionValues(newValues);
    const newIsValid = editableKeys.every(k => newValues[k]?.trim());
    const newInstruction = assembleInstruction(newValues);
    setCurrentInstruction(newInstruction);
    setIsValid(newIsValid);
    onChange({
      instruction: newInstruction,
      isValid: newIsValid,
    });
  };

  const handleSave = () => {
    if (!visitId || !patientGoalId) return;
    const instructionToSave = isFreeText
      ? freeTextInstruction.trim()
      : currentInstruction;
    if (!instructionToSave) return;
    setIsSaving(true);
    addInterventionToVisit({
      path: { visit_id: visitId },
      body: {
        intervention_template_id: isFreeText ? null : template.id,
        patient_goal_id: patientGoalId,
        name: isFreeText ? 'Free Text' : template.name,
        instruction: instructionToSave,
      },
      throwOnError: true,
    })
      .then(() => {
        onSave?.();
      })
      .catch(() => {
        showError('Failed to save intervention', 'Please try again');
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  return (
    <Card className='w-full p-2.5 sm:p-2.5 md:p-2.5 bg-background border-border/70'>
      <CardHeader className='mb-2 relative pr-8 p-0'>
        <CardTitle className='text-sm font-semibold leading-tight'>
          <span className='text-muted-foreground font-medium'>
            Intervention:{' '}
          </span>
          <span className='font-semibold'>
            {isFreeText ? 'Free Text' : template.name}
          </span>
        </CardTitle>
        {onCancel && (
          <button
            type='button'
            onClick={onCancel}
            className='absolute top-0 right-0 p-1 rounded text-muted-foreground hover:text-foreground hover:bg-muted transition-colors'
            aria-label='Discard and remove'
          >
            <X className='h-4 w-4' />
          </button>
        )}
      </CardHeader>

      <CardContent className='space-y-1.5 p-0'>
        <p className='text-xs font-medium uppercase tracking-wide text-muted-foreground'>
          Instruction
        </p>
        <div className='leading-relaxed'>
          {isFreeText ? (
            <Textarea
              placeholder='Enter intervention instruction...'
              value={freeTextInstruction}
              onChange={handleFreeTextInstructionChange}
              textareaSize='default'
              className='min-h-[64px]'
            />
          ) : (
            renderEditableInstructionFields(
              template.instruction,
              instructionValues,
              handleInstructionChange
            )
          )}
        </div>
        {showSaveButton && (
          <div className='flex justify-end pt-1'>
            <Button
              size='sm'
              disabled={
                (isFreeText ? !freeTextInstruction.trim() : !isValid) ||
                isSaving
              }
              onClick={handleSave}
            >
              {isSaving ? (
                <>
                  <Loader2 className='h-3 w-3 animate-spin mr-1' />
                  Saving...
                </>
              ) : (
                'Save'
              )}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default CreateInterventionFromTemplateCard;
